import axios from 'axios'
import { useAuthStore } from '../store/authStore'

export const api = axios.create({
  baseURL: '/api/v1',
  withCredentials: true,
})

// Attach access token to every request
api.interceptors.request.use((config) => {
  const token = useAuthStore.getState().accessToken
  if (token) {
    config.headers.Authorization = `Bearer ${token}`
  }
  return config
})

// On 401, try refresh then retry once
let isRefreshing = false
let refreshQueue: Array<(token: string) => void> = []

api.interceptors.response.use(
  (res) => res,
  async (error) => {
    const original = error.config
    if (error.response?.status !== 401 || original._retry) {
      return Promise.reject(error)
    }
    original._retry = true

    if (isRefreshing) {
      return new Promise((resolve) => {
        refreshQueue.push((token) => {
          original.headers.Authorization = `Bearer ${token}`
          resolve(api(original))
        })
      })
    }

    isRefreshing = true
    try {
      const { data } = await axios.post('/api/v1/auth/refresh', {}, { withCredentials: true })
      const newToken: string = data.access_token
      useAuthStore.getState().setAccessToken(newToken, data.role ?? useAuthStore.getState().role ?? '', data.plan ?? useAuthStore.getState().plan ?? undefined, data.language)
      refreshQueue.forEach((cb) => cb(newToken))
      refreshQueue = []
      original.headers.Authorization = `Bearer ${newToken}`
      return api(original)
    } catch {
      useAuthStore.getState().logout()
      window.location.href = '/login'
      return Promise.reject(error)
    } finally {
      isRefreshing = false
    }
  },
)

// ── Types ────────────────────────────────────────────────────────────────────

export interface Instance {
  id: string
  name: string
  subdomain: string
  custom_domain?: string
  odoo_version: string
  status: 'pending_approval' | 'provisioning' | 'initializing' | 'updating' | 'restarting' | 'running' | 'stopped' | 'error' | 'deleting' | 'restoring' | 'replacing_filestore'
  status_message?: string
  odoo_container_name: string
  odoo_container_id?: string
  pg_container_name: string
  pg_container_id?: string
  db_name: string
  created_at: string
  updated_at: string
  subdomain_url: string
  custom_url?: string
  created_by?: string
  created_by_email?: string
  auto_db_restore_on_failure: boolean
  // True once a customer-supplied Odoo Enterprise tree is mounted for this
  // instance (read-only, never shared with any other instance).
  enterprise_enabled: boolean
  label_id?: string
  label?: InstanceLabel
  odoo_image_ref: string
  odoo_image_id?: string
  pg_image_ref: string
  previous_odoo_image_ref?: string
}

export interface InstanceLabel {
  id: string
  name: string
  color: string
  created_at?: string
}

export interface SecurityFinding {
  file: string
  line: number
  label: string
  snippet: string
  severity: 'critical' | 'warning'
}

export interface Addon {
  id: string
  instance_id: string
  name: string
  is_default: boolean
  status: 'uploaded' | 'pending_review' | 'queued' | 'installing' | 'installed' | 'failed' | 'updating' | 'update_failed'
  source_url?: string
  installed_at: string
  version?: string
  author?: string
  summary?: string
  license?: string
  website?: string
  source_id?: string
  security_findings?: SecurityFinding[]
}

export interface AddonJob {
  id: string
  instance_id: string
  addon_id: string | null
  action: 'install' | 'update'
  status: 'running' | 'success' | 'failed' | 'rolled_back'
  logs: string
  exit_code?: number
  created_at: string
  updated_at: string

  // Source provenance, present on jobs that re-pulled from git. `changed`
  // distinguishes "new code was deployed" from "the code already on disk was
  // upgraded again" — the Odoo upgrade succeeds either way.
  source_ref?: string
  previous_commit?: string
  pulled_commit?: string
  changed?: boolean
}

export interface PlatformUser {
  id: string
  email: string
  // Customer identity for the BTK 5651 domain report: company/trade name
  // (ünvan) and contact phone. Empty string = not provided.
  company_name: string
  phone: string
  role: string
  plan: string
  // ISO timestamp when a time-limited admin grant lapses; absent = permanent.
  plan_expires_at?: string
  instance_limit: number
  instance_count: number
  language: string
  status: 'active' | 'pending' | 'deactivated'
  last_login?: string
  deactivated_at?: string
  created_at: string
  updated_at: string
  notify_backup_failed: boolean
  notify_provision_failed: boolean
  notify_instance_ready: boolean
  marketing_emails: boolean
  // mail_enabled retained for back-compat with /auth/me etc; not surfaced
  // as a UI toggle on the Users page anymore.
  mail_enabled?: boolean
  // In-app usage measurement (legitimate interest); user opts out on the
  // Profile page. Optional so test fixtures predating the field stay valid.
  usage_tracking_enabled?: boolean
  mail_has_paid_sub: boolean
  mail_blocked: boolean
  // Admin-granted: reveals the Enterprise upload surface and raises the
  // addon upload cap. Nothing to do with the platform's own editions.
  enterprise_enabled: boolean
  has_password: boolean
  github_username?: string
  github_connected_at?: string
}

export interface PaginatedUsers {
  items: PlatformUser[]
  total: number
  page: number
  page_size: number
}

export interface UserListParams {
  page?: number
  page_size?: number
  q?: string
  status?: 'active' | 'pending' | ''
  plan?: 'free' | 'paid' | ''
  sort?: 'email' | 'status' | 'plan' | 'instance_count' | 'created_at' | 'last_login'
  order?: 'asc' | 'desc'
}

export interface GitHubRepo {
  full_name: string
  private: boolean
  default_branch: string
  description?: string
  url: string
}

export const githubApi = {
  disconnect: () => api.post('/github/disconnect'),
  repos: (page = 1, q = '') =>
    api.get<GitHubRepo[]>('/github/repos', { params: { page, ...(q ? { q } : {}) } }).then((r) => r.data),
  // Public: returns the GitHub OAuth authorization URL for sign-in/register.
  authUrl: () => axios.get<{ url: string }>('/api/v1/auth/github/url').then((r) => r.data),
}

export interface Backup {
  id: string
  instance_id: string
  filename: string
  size_bytes: number
  db_size_bytes?: number
  filestore_size_bytes?: number
  mail_archive_size_bytes?: number
  trigger: 'manual' | 'auto' | 'upload' | 'pre_op'
  status: 'running' | 'done' | 'failed'
  error?: string
  created_at: string
}

export interface ScriptRun {
  id: string
  instance_id: string
  script_content: string
  status: 'pending' | 'running' | 'success' | 'failed' | 'timeout'
  output: string
  exit_code?: number
  backup_created: boolean
  created_at: string
  duration_ms?: number
}

export interface ResourceConfig {
  memory_limit_mb?: number
  cpu_cores?: number
  pids_limit?: number
}

export interface ResourceConfigEffective {
  memory_limit_mb: number
  cpu_cores: number
  pids_limit: number
  is_overridden: boolean
}

export interface ResourcesResponse {
  odoo: ResourceConfigEffective
  pg: ResourceConfigEffective
}

export interface InstanceResourceStats {
  instance_id: string
  instance_name: string
  subdomain: string
  status: string
  cpu_percent: number
  memory_usage: number
  memory_limit: number
}

export interface UserResourceStats {
  user_id: string
  user_email: string
  instances: InstanceResourceStats[]
  total_cpu_percent: number
  total_memory_usage: number
  instance_count: number
  running_count: number
}

export interface HostResourceStats {
  cpu_percent: number
  mem_total: number
  mem_used: number
  mem_free: number
  disk_total: number
  disk_used: number
  disk_free: number
  disk_path: string
}

// ── API helpers ──────────────────────────────────────────────────────────────

export type TemplateModuleSource = 'github' | 'builtin' | 'zip'

export interface TemplateModule {
  id: string
  template_id: string
  source: TemplateModuleSource
  github_url?: string
  branch?: string
  subdirectory?: string
  archive_sha?: string
  module_name: string
  description?: string
  created_at: string
}

export interface ModuleTemplate {
  id: string
  name: string
  description?: string
  is_active: boolean
  is_default: boolean
  // Series the template was built for; absent = any. Captured templates take
  // the source instance's series.
  odoo_version?: string
  modules?: TemplateModule[]
  created_at: string
  updated_at: string
}

export interface TemplateValidationResult {
  module_name: string
  ok: boolean
  error?: string
}

export interface TemplateJob {
  id: string
  instance_id: string
  template_id?: string
  module_name: string
  source: TemplateModuleSource
  github_url?: string
  branch?: string
  subdirectory?: string
  archive_sha?: string
  status: 'queued' | 'downloading' | 'installing' | 'success' | 'failed'
  logs: string
  exit_code?: number
  attempt: number
  created_at: string
  updated_at: string
}

export interface CreateTemplateFromInstanceResponse {
  template: ModuleTemplate
  skipped_modules: string[]
  installed_count: number
  captured_count: number
}

export type ModuleSummary = {
  name: string
  install_count: number
  github_count: number
  upload_count: number
  authors: string[]
  latest_version: string
  first_installed_at?: string
  last_installed_at?: string
}

export type ModuleInstallation = {
  addon_id: string
  name: string
  version: string
  author: string
  status: string
  installed_at: string
  source_url?: string
  source_repo_url?: string
  source_ref?: string
  archive_sha?: string
  has_stored_archive: boolean

  instance_id: string
  subdomain: string
  instance_name: string
  custom_domain?: string

  owner_id?: string
  owner_email?: string
}

export const modulesApi = {
  list: (q?: string) =>
    api.get<ModuleSummary[]>('/admin/modules', { params: q ? { q } : undefined }).then((r) => r.data),
  installations: (name: string) =>
    api
      .get<ModuleInstallation[]>(`/admin/modules/${encodeURIComponent(name)}/installations`)
      .then((r) => r.data),
  downloadArchive: async (addonId: string, suggestedName: string) => {
    const r = await api.get(`/admin/modules/installations/${addonId}/archive`, {
      responseType: 'blob',
    })
    const url = URL.createObjectURL(r.data as Blob)
    const a = document.createElement('a')
    a.href = url
    a.download = `${suggestedName}.zip`
    document.body.appendChild(a)
    a.click()
    a.remove()
    setTimeout(() => URL.revokeObjectURL(url), 1000)
  },
}

export const templatesApi = {
  list: () => api.get<ModuleTemplate[]>('/templates').then((r) => r.data),
  listAll: () => api.get<ModuleTemplate[]>('/templates/all').then((r) => r.data),
  get: (id: string) => api.get<ModuleTemplate>(`/templates/${id}`).then((r) => r.data),
  create: (body: { name: string; description?: string; is_active?: boolean; odoo_version?: string }) =>
    api.post<ModuleTemplate>('/templates', body).then((r) => r.data),
  update: (id: string, body: { name?: string; description?: string; is_active?: boolean; odoo_version?: string }) =>
    api.put<ModuleTemplate>(`/templates/${id}`, body).then((r) => r.data),
  deactivate: (id: string) => api.delete(`/templates/${id}`),
  delete: (id: string) => api.delete(`/templates/${id}/hard`),
  addModule: (id: string, body: { source?: TemplateModuleSource; github_url?: string; branch?: string; subdirectory?: string; archive_sha?: string; module_name: string; description?: string }) =>
    api.post<TemplateModule>(`/templates/${id}/modules`, body).then((r) => r.data),
  removeModule: (id: string, moduleId: string) => api.delete(`/templates/${id}/modules/${moduleId}`),
  setDefault: (id: string) => api.post(`/admin/templates/${id}/set-default`).then((r) => r.data),
  validate: (id: string) =>
    api.post<TemplateValidationResult[]>(`/admin/templates/${id}/validate`).then((r) => r.data),
  usage: (id: string) =>
    api.get<{ count: number }>(`/admin/templates/${id}/usage`).then((r) => r.data),
  /** Turn git-referenced modules into frozen archives taken from an installed copy. */
  freeze: (id: string) =>
    api.post<{ frozen: string[]; missing: string[] }>(`/admin/templates/${id}/freeze`).then((r) => r.data),
  createFromInstance: (instanceId: string, body: { name: string; description?: string }) =>
    api.post<CreateTemplateFromInstanceResponse>(`/instances/${instanceId}/template`, body).then((r) => r.data),
}

export const instancesApi = {
  list: () => api.get<Instance[]>('/instances').then((r) => r.data),
  get: (id: string) => api.get<Instance>(`/instances/${id}`).then((r) => r.data),
  validate: (body: { name: string; subdomain: string }) =>
    api.post<{ ok: boolean }>('/instances/validate', body).then((r) => r.data),
  create: (body: { name: string; subdomain: string; odoo_version: string; pg_version?: string; template_id?: string; assigned_to?: string; sector?: string; user_count?: string; request_reason?: string }) =>
    api.post<{ id: string; status: string; subdomain_url: string }>('/instances', body).then((r) => r.data),
  resetAdminPassword: (id: string, newPassword: string, confirmPassword: string) =>
    api.post(`/instances/${id}/reset-admin-password`, { new_password: newPassword, confirm_password: confirmPassword }),
  delete: (id: string) => api.delete(`/instances/${id}`),
  start: (id: string) => api.post(`/instances/${id}/start`),
  stop: (id: string) => api.post(`/instances/${id}/stop`),
  restart: (id: string) => api.post(`/instances/${id}/restart`),
  retry: (id: string) => api.post(`/instances/${id}/retry`),
  recreate: (id: string) => api.post(`/instances/${id}/recreate`),
  credentials: (id: string) =>
    api.get<{ login: string; admin_password: string }>(`/instances/${id}/credentials`).then((r) => r.data),
  deployKey: (id: string) =>
    api.get<{ public_key: string; key_type: string; label: string }>(`/instances/${id}/deploy-key`).then((r) => r.data),
  checkSubdomain: (subdomain: string) =>
    api.get<Instance[]>(`/instances`).then((r) =>
      r.data.some((i: Instance) => i.subdomain === subdomain)
    ),
  assignOwner: (instanceId: string, userId: string) =>
    api.patch(`/instances/${instanceId}/owner`, { user_id: userId }),
  updateSettings: (instanceId: string, settings: { auto_db_restore_on_failure?: boolean }) =>
    api.patch<Instance>(`/instances/${instanceId}/settings`, settings).then((r) => r.data),
  clone: (id: string, body: { name: string; subdomain: string }) =>
    api.post<{ id: string; status: string; subdomain_url: string }>(`/instances/${id}/clone`, body).then((r) => r.data),
  getResources: (id: string) =>
    api.get<ResourcesResponse>(`/instances/${id}/resources`).then((r) => r.data),
  updateResources: (id: string, body: { odoo?: ResourceConfig | null; pg?: ResourceConfig | null }) =>
    api.put<ResourcesResponse>(`/instances/${id}/resources`, body).then((r) => r.data),
  listTemplateJobs: (id: string) =>
    api.get<TemplateJob[]>(`/instances/${id}/template-jobs`).then((r) => r.data),
  setLabel: (instanceId: string, labelId: string | null) =>
    api.patch(`/instances/${instanceId}/label`, { label_id: labelId }),
}

export const labelsApi = {
  list: () => api.get<InstanceLabel[]>('/labels').then((r) => r.data),
  create: (body: { name: string; color: string }) =>
    api.post<InstanceLabel>('/labels', body).then((r) => r.data),
  update: (id: string, body: { name: string; color: string }) =>
    api.patch<InstanceLabel>(`/labels/${id}`, body).then((r) => r.data),
  delete: (id: string) => api.delete(`/labels/${id}`),
  reorder: (ids: string[]) => api.post('/labels/reorder', { ids }),
}

export interface PendingApproval {
  id: string
  name: string
  subdomain: string
  odoo_version: string
  pg_version: string
  sector?: string
  user_count?: string
  request_reason?: string
  created_by?: string
  created_by_email?: string
  created_at: string
}

export const approvalsApi = {
  listPending: () => api.get<PendingApproval[]>('/admin/instances/pending').then((r) => r.data),
  approve: (id: string) => api.post(`/admin/instances/${id}/approve`),
  reject: (id: string) => api.post(`/admin/instances/${id}/reject`),
}

export interface EnterpriseBundle {
  id: string
  instance_id: string
  filename: string
  archive_sha: string
  size_bytes: number
  module_count: number
  uploaded_by?: string
  uploaded_by_email?: string
  uploaded_at: string
  status: string
}

// Bring-your-own Odoo Enterprise. The customer holds the licence and supplies
// the archive; it is placed in their own instance alone. There is deliberately
// no sync/pull here — a bundle is replaced by uploading a new archive.
export const enterpriseApi = {
  get: (id: string) =>
    api
      .get<{ bundle: EnterpriseBundle | null; enterprise_enabled: boolean; can_upload: boolean }>(`/instances/${id}/enterprise`)
      .then((r) => r.data),
  upload: (id: string, file: File, onProgress?: (pct: number) => void, signal?: AbortSignal) => {
    const form = new FormData()
    form.append('file', file)
    return api
      .post<{ bundle: EnterpriseBundle; modules: number; warning?: string }>(
        `/instances/${id}/enterprise`,
        form,
        {
          onUploadProgress: (e) => {
            if (onProgress && e.total) onProgress(Math.round((e.loaded / e.total) * 100))
          },
          signal,
        }
      )
      .then((r) => r.data)
  },
  remove: (id: string) => api.delete(`/instances/${id}/enterprise`).then((r) => r.data),
}

export const addonsApi = {
  list: (id: string) => api.get<Addon[]>(`/instances/${id}/addons`).then((r) => r.data),
  upload: (id: string, file: File, onProgress?: (pct: number) => void, signal?: AbortSignal) => {
    const form = new FormData()
    form.append('file', file)
    return api.post<{ addons: Addon[]; security_findings: SecurityFinding[]; message?: string }>(`/instances/${id}/addons`, form, {
      onUploadProgress: (e) => {
        if (onProgress && e.total) onProgress(Math.round((e.loaded / e.total) * 100))
      },
      signal,
    }).then((r) => r.data)
  },
  installFromGitHub: (id: string, githubUrl: string, ref: string, auth: GithubAuthMode = 'public', modules?: string[]) =>
    api.post<{ addons: Addon[]; jobs: AddonJob[]; security_findings: SecurityFinding[] }>(
      `/instances/${id}/addons/github`,
      { github_url: githubUrl, ref, auth, ...(modules && modules.length ? { modules } : {}) }
    ).then((r) => r.data),
  // Cheap URL-entry probe: branch list + default branch + OCA flag, no archive
  // download. Used to turn the branch input into a <select> immediately.
  githubBranches: (id: string, githubUrl: string, auth: GithubAuthMode = 'public') =>
    api.post<GitHubBranches>(
      `/instances/${id}/addons/github/branches`,
      { github_url: githubUrl, auth }
    ).then((r) => r.data),
  // Preview a GitHub repo before install: module folders (with manifest metadata)
  // for the given ref so the UI can render the checkbox picker. Downloads the archive.
  previewGitHub: (id: string, githubUrl: string, ref: string, auth: GithubAuthMode = 'public') =>
    api.post<GitHubPreview>(
      `/instances/${id}/addons/github/preview`,
      { github_url: githubUrl, ref, auth }
    ).then((r) => r.data),
  install: (instanceId: string, addonId: string) =>
    api.post<{ job: AddonJob }>(`/instances/${instanceId}/addons/${addonId}/install`).then((r) => r.data),
  update: (instanceId: string, addonId: string, file?: File, githubUrl?: string, ref?: string, auth: GithubAuthMode = 'public', onProgress?: (pct: number) => void, signal?: AbortSignal, moduleName?: string) => {
    if (file) {
      const form = new FormData()
      form.append('file', file)
      if (moduleName) form.append('module_name', moduleName)
      return api.post<{ job: AddonJob }>(`/instances/${instanceId}/addons/${addonId}/update`, form, {
        onUploadProgress: (e) => {
          if (onProgress && e.total) onProgress(Math.round((e.loaded / e.total) * 100))
        },
        signal,
      }).then((r) => r.data)
    }
    return api.post<{ job: AddonJob }>(
      `/instances/${instanceId}/addons/${addonId}/update`,
      githubUrl ? { github_url: githubUrl, ref, auth, module_name: moduleName } : undefined,
      { signal }
    ).then((r) => r.data)
  },
  sync: (instanceId: string, addonId: string) =>
    api.post<{ job: AddonJob }>(`/instances/${instanceId}/addons/${addonId}/sync`).then((r) => r.data),
  listJobs: (instanceId: string, addonId: string) =>
    api.get<AddonJob[]>(`/instances/${instanceId}/addons/${addonId}/jobs`).then((r) => r.data),
  delete: (instanceId: string, addonId: string) =>
    api.delete(`/instances/${instanceId}/addons/${addonId}`),
  approve: (instanceId: string, addonId: string) =>
    api.post<Addon>(`/instances/${instanceId}/addons/${addonId}/approve`).then((r) => r.data),
  reject: (instanceId: string, addonId: string) =>
    api.post<{ status: string }>(`/instances/${instanceId}/addons/${addonId}/reject`).then((r) => r.data),
}

export type GithubAuthMode = 'public' | 'oauth' | 'deploy_key'

export interface GitHubPreviewModule {
  name: string
  manifest_name: string
  version: string
  depends: string[]
  already_installed: boolean
}

export interface GitHubBranches {
  owner: string
  repo: string
  is_oca: boolean
  default_branch: string
  branches: string[]
}

export interface GitHubPreview {
  owner: string
  repo: string
  is_oca: boolean
  default_branch: string
  branches: string[]
  modules: GitHubPreviewModule[]
}

export interface UserDeployKey {
  id: string
  repo_url: string
  public_key: string
  fingerprint: string
  created_at: string
  last_used_at?: string
}

export const deployKeysApi = {
  list: () => api.get<UserDeployKey[]>('/me/deploy-keys').then((r) => r.data),
  ensure: (repoUrl: string) =>
    api.post<{ key: UserDeployKey; created: boolean }>('/me/deploy-keys', { repo_url: repoUrl }).then((r) => r.data),
  delete: (id: string) => api.delete(`/me/deploy-keys/${id}`),
}

export const domainsApi = {
  bind: (id: string, domain: string) =>
    api.post(`/instances/${id}/domains`, { domain }).then((r) => r.data),
  unbind: (id: string, domain: string) =>
    api.delete(`/instances/${id}/domains/${domain}`),
}

export interface MailDomain {
  id: string
  instance_id: string
  domain: string
  dkim_selector: string
  dkim_public_key: string
  mx_verified: boolean
  spf_verified: boolean
  dkim_verified: boolean
  dmarc_verified: boolean
  ptr_verified: boolean
  enabled: boolean
  approval_status: 'pending' | 'approved' | 'rejected'
  created_at: string
}
// Mailboxes ARE the SMTP submission credentials (post-068 unification).
// The same row authenticates to Postfix submission and (with the encrypted
// password) is re-injected into Odoo via /mail/users/:user_id/wire-odoo.
export interface MailUser {
  id: string
  domain_id: string
  local_part: string
  quota_mb: number
  enabled: boolean
  daily_send_limit: number | null
  last_used_at?: string | null
  bounce_count_24h: number
  soft_bounce_count_24h: number
  is_catchall: boolean
  auto_disabled_at?: string | null
  auto_disable_reason?: string | null
  created_at: string
}
export interface MailMailboxCreated {
  user: MailUser
  password: string
  smtp: { host: string; port: number; encryption: string; username: string }
}
export interface MailSendEvent {
  id: number
  user_id: string
  recipient: string
  decision: 'accepted' | 'rejected' | 'deferred'
  reason?: string
  ts: string
  local_part?: string
  domain?: string
}
export interface MailUsage {
  date: string
  sent_today: number
  daily_limit: number
  remaining: number
  storage_bytes: number
  storage_limit_bytes: number
}
export interface MailAdminCounters {
  total_domains: number
  approved_domains: number
  pending_domains: number
  unverified_dns: number
  total_mailboxes: number
  auto_disabled_mailboxes: number
  blocked_users: number
  active_subscriptions: number
  past_due_subs: number
  sent_today: number
  bounces_last_24h: number
  soft_bounces_last_24h: number
}
export interface MailAdminTopSender {
  user_id: string
  email: string
  instance_name: string
  subdomain: string
  sent_today: number
  bounce_count_24h: number
}
export interface MailAdminFlaggedMailbox {
  mailbox_id: string
  user_id: string
  email: string
  instance_subdomain: string
  mailbox: string
  bounce_count_24h: number
  auto_disabled_at?: string | null
  auto_disable_reason?: string | null
  owner_blocked: boolean
}
export interface MailAdminUnverifiedDomain {
  domain_id: string
  domain: string
  instance_subdomain: string
  owner_email: string
  mx: boolean
  spf: boolean
  dkim: boolean
  dmarc: boolean
  ptr: boolean
  approval_status: string
}
export interface MailPostfixQueueStats {
  total: number
  by_queue: Record<string, number>
  oldest_age_seconds: number
  total_bytes: number
  postfix_down: boolean
  error?: string
}
export interface MailInternalInboxCounts {
  postmaster: number
  abuse: number
  tlsrpt: number
}
export interface MailAdminOverview {
  counters: MailAdminCounters
  top_senders: MailAdminTopSender[] | null
  flagged_mailboxes: MailAdminFlaggedMailbox[] | null
  unverified_domains: MailAdminUnverifiedDomain[] | null
  queue: MailPostfixQueueStats
  internal_inboxes: MailInternalInboxCounts
}

export interface MailDNSPayload {
  domain: string
  selector: string
  dkim_host: string
  dmarc_host: string
  records: { mx: string; spf: string; dkim: string; dmarc: string }
}

export const mailApi = {
  listDomains: (id: string) =>
    api.get<MailDomain[]>(`/instances/${id}/mail/domains`).then((r) => r.data),
  createDomain: (id: string, domain: string) =>
    api.post<MailDomain>(`/instances/${id}/mail/domains`, { domain }).then((r) => r.data),
  deleteDomain: (id: string, domainId: string) =>
    api.delete(`/instances/${id}/mail/domains/${domainId}`),
  dnsRecords: (id: string, domainId: string) =>
    api.get<MailDNSPayload>(`/instances/${id}/mail/domains/${domainId}/dns-records`).then((r) => r.data),
  verifyDomain: (id: string, domainId: string) =>
    api.post(`/instances/${id}/mail/domains/${domainId}/verify`).then((r) => r.data),

  listMailboxes: (id: string) =>
    api.get<MailUser[]>(`/instances/${id}/mail/users`).then((r) => r.data),
  createMailbox: (id: string, body: { domain_id: string; local_part: string; password?: string; quota_mb?: number }) =>
    api.post<MailMailboxCreated>(`/instances/${id}/mail/users`, body).then((r) => r.data),
  deleteMailbox: (id: string, userId: string) =>
    api.delete(`/instances/${id}/mail/users/${userId}`),
  wireMailboxToOdoo: (id: string, userId: string) =>
    api.post<{ wired: boolean; username: string }>(`/instances/${id}/mail/users/${userId}/wire-odoo`).then((r) => r.data),
  rotateMailboxPassword: (id: string, userId: string) =>
    api.post<{ password: string; smtp: { host: string; port: number; encryption: string; username: string } }>(
      `/instances/${id}/mail/users/${userId}/rotate-password`,
    ).then((r) => r.data),
  setMailboxCatchall: (id: string, userId: string, enabled: boolean) =>
    api.patch(`/instances/${id}/mail/users/${userId}/catch-all`, { enabled }),
  events: (id: string, params?: { limit?: number; before?: string }) =>
    api.get<MailSendEvent[]>(`/instances/${id}/mail/events`, { params }).then((r) => r.data),

  usage: (id: string) =>
    api.get<MailUsage>(`/instances/${id}/mail/usage`).then((r) => r.data),

  startCheckout: (instanceId: string) =>
    api.post<{ token: string; payment_page_url: string }>(`/billing/mail`, null, { params: { instance_id: instanceId } }).then((r) => r.data),

  // Admin
  adminOverview: () =>
    api.get<MailAdminOverview>('/admin/mail/overview').then((r) => r.data),
  adminListApprovals: () =>
    api.get<Array<{ id: string; domain_id: string; status: string; admin_note: string; requested_at: string }>>('/admin/mail/approval-queue').then((r) => r.data),
  adminApprove: (id: string, note?: string) =>
    api.post(`/admin/mail/approval-queue/${id}/approve`, { note: note ?? '' }),
  adminReject: (id: string, note?: string) =>
    api.post(`/admin/mail/approval-queue/${id}/reject`, { note: note ?? '' }),
  // Lifts an auto-disable + resets the 24h bounce counter without
  // rotating the SMTP password — admin uses this when a bounce burst was
  // a tenant config issue, not abuse, and they want to bring the
  // mailbox back without forcing the tenant to update odoo creds.
  adminClearMailboxDisable: (mailboxId: string) =>
    api.post(`/admin/mail/mailboxes/${mailboxId}/clear-disable`),
  adminListTLSReports: (limit = 25) =>
    api.get<MailTLSReport[]>('/admin/mail/tls-reports', { params: { limit } }).then((r) => r.data),
  adminListDMARCReports: (limit = 25) =>
    api.get<MailDMARCReport[]>('/admin/mail/dmarc-reports', { params: { limit } }).then((r) => r.data),
  adminGetKillswitch: () =>
    api.get<{ enabled: boolean }>('/admin/mail/killswitch').then((r) => r.data),
  adminSetKillswitch: (enabled: boolean) =>
    api.post('/admin/mail/killswitch', { enabled }),
}

export interface MailDMARCReport {
  id: string
  received_at: string
  organization_name?: string
  contact_email?: string
  report_id?: string
  policy_domain: string
  date_range_start?: string
  date_range_end?: string
  total_messages: number
  pass_count: number
  fail_count: number
}
export interface MailTLSReport {
  id: string
  received_at: string
  organization_name?: string
  contact_info?: string
  report_id?: string
  policy_domain: string
  date_range_start?: string
  date_range_end?: string
  total_session_count: number
  failed_session_count: number
}

export const usersApi = {
  list: (params: UserListParams = {}) => {
    const qp: Record<string, string> = {}
    if (params.page) qp.page = String(params.page)
    if (params.page_size) qp.page_size = String(params.page_size)
    if (params.q) qp.q = params.q
    if (params.status) qp.status = params.status
    if (params.plan) qp.plan = params.plan
    if (params.sort) qp.sort = params.sort
    if (params.order) qp.order = params.order
    return api.get<PaginatedUsers>('/users', { params: qp }).then((r) => r.data)
  },
  // listAll fetches every user in one request — for dropdowns/selects where
  // server-side search is not yet wired up. Capped at 1000 by the backend.
  // Swap to a searchable typeahead once user counts exceed that.
  listAll: () =>
    api.get<PaginatedUsers>('/users', { params: { page_size: 1000 } }).then((r) => r.data.items),
  create: (body: { email: string; instance_limit: number; plan?: string; language?: string; company_name?: string; phone?: string }) =>
    api.post<PlatformUser>('/users', body).then((r) => r.data),
  updateLimit: (id: string, instance_limit: number) =>
    api.patch(`/users/${id}`, { instance_limit }),
  // Company name (ünvan) + phone — the customer identity behind the account,
  // needed for the monthly BTK 5651 hosted-domain report.
  updateContact: (id: string, body: { company_name?: string; phone?: string }) =>
    api.patch(`/users/${id}`, body),
  // updatePlan optionally sets a time-limited grant expiry. Pass a
  // "YYYY-MM-DD" date to grant until that day, '' to clear (permanent), or
  // undefined to leave the expiry untouched.
  updatePlan: (id: string, plan: string, planExpiresAt?: string) =>
    api.patch(`/users/${id}`, {
      plan,
      ...(planExpiresAt !== undefined ? { plan_expires_at: planExpiresAt } : {}),
    }),
  setPlanExpiry: (id: string, planExpiresAt: string) =>
    api.patch(`/users/${id}`, { plan_expires_at: planExpiresAt }),
  updateMailEnabled: (id: string, mail_enabled: boolean) =>
    api.patch(`/users/${id}`, { mail_enabled }),
  // Atomic mail-access PATCH — sets both mail_enabled and mail_blocked in
  // one request so the UI toggle always converges in a single click even
  // when both fields need to flip (e.g. unblock + grant comp together).
  updateMailAccess: (id: string, body: { mail_enabled?: boolean; mail_blocked?: boolean }) =>
    api.patch(`/users/${id}`, body),
  updateEnterprise: (id: string, enterprise_enabled: boolean) =>
    api.patch(`/users/${id}`, { enterprise_enabled }),
  updateMailBlocked: (id: string, mail_blocked: boolean) =>
    api.patch(`/users/${id}`, { mail_blocked }),
  updateLanguage: (id: string, language: string) =>
    api.patch(`/users/${id}`, { language }),
  updateNotifications: (id: string, prefs: Partial<Pick<PlatformUser, 'notify_backup_failed' | 'notify_provision_failed' | 'notify_instance_ready' | 'marketing_emails'>>) =>
    api.patch(`/users/${id}`, prefs),
  resendInvite: (id: string) => api.post(`/users/${id}/invite`),
  deactivate: (id: string) => api.post(`/users/${id}/deactivate`),
  activate: (id: string) => api.post(`/users/${id}/activate`),
  setPassword: (id: string, password: string) =>
    api.post(`/users/${id}/set-password`, { password }),
  sendPasswordReset: (id: string) => api.post(`/users/${id}/send-password-reset`),
  delete: (id: string) => api.delete(`/users/${id}`),
  stats: () => api.get<UserResourceStats[]>('/users/stats').then((r) => r.data),
  hostStats: () => api.get<HostResourceStats>('/host/stats').then((r) => r.data),
  // BTK 5651 monthly hosted-domains CSV ("Barındırılan Alan Adları"),
  // generated server-side from live instance/mail domains + account identity.
  btkDomainsCsv: () =>
    api.get('/admin/btk/domains-csv', { responseType: 'blob' }).then((r) => r.data as Blob),
}

export const backupsApi = {
  list: (instanceId: string) =>
    api.get<Backup[]>(`/instances/${instanceId}/backups`).then((r) => r.data),
  create: (instanceId: string) =>
    api.post<Backup>(`/instances/${instanceId}/backups`).then((r) => r.data),
  upload: (instanceId: string, file: File) => {
    const form = new FormData()
    form.append('file', file)
    return api.post<Backup>(`/instances/${instanceId}/backups/upload`, form, {
      headers: { 'Content-Type': 'multipart/form-data' },
    }).then((r) => r.data)
  },
  download: (instanceId: string, backupId: string, part: 'db' | 'filestore' = 'db') =>
    api.get(`/instances/${instanceId}/backups/${backupId}/download`, { params: { part }, responseType: 'blob' }).then((r) => r.data),
  restore: (instanceId: string, backupId: string, neutralize = false) =>
    api.post(`/instances/${instanceId}/backups/${backupId}/restore`, { neutralize }),
  delete: (instanceId: string, backupId: string) =>
    api.delete(`/instances/${instanceId}/backups/${backupId}`),
  replaceFilestore: (instanceId: string, file: File) => {
    const form = new FormData()
    form.append('filestore', file)
    return api.post(`/instances/${instanceId}/filestore/replace`, form, {
      headers: { 'Content-Type': 'multipart/form-data' },
    })
  },
  // Odoo's own backup archive (dump.sql + filestore/ + manifest.json), replayed
  // onto an existing instance. Not the same shape as upload(), which takes the
  // platform's pg_dump.
  importOdooZip: (instanceId: string, file: File, neutralize = false) => {
    const form = new FormData()
    form.append('file', file)
    form.append('neutralize', String(neutralize))
    return api.post(`/instances/${instanceId}/import`, form, {
      headers: { 'Content-Type': 'multipart/form-data' },
    })
  },
}

export const scriptsApi = {
  run: (instanceId: string, script: string, backupFirst: boolean) =>
    api.post<ScriptRun>(`/instances/${instanceId}/scripts/run`, { script, backup_first: backupFirst }).then((r) => r.data),
  get: (instanceId: string, runId: string) =>
    api.get<ScriptRun>(`/instances/${instanceId}/scripts/${runId}`).then((r) => r.data),
  list: (instanceId: string) =>
    api.get<ScriptRun[]>(`/instances/${instanceId}/scripts`).then((r) => r.data),
}

// ── Billing types ─────────────────────────────────────────────────────────────

export interface PlanInfo {
  id: string
  name: string
  instance_limit: number
  price_monthly: number
  features: string[]
}

// Fixed per-instance pricing. TRY is what Iyzico actually charges; USD and EUR
// are display-only stickers. All are fixed, not FX-derived.
export interface InstancePricing {
  monthly_usd: number
  yearly_usd: number
  monthly_eur: number
  yearly_eur: number
  monthly_try: number
  yearly_try: number
}

export interface MailPricing {
  monthly_usd: number
  yearly_usd: number
  monthly_eur: number
  yearly_eur: number
  monthly_try: number
  yearly_try: number
}

export interface PlansResponse {
  plans: PlanInfo[]
  instance: InstancePricing
  mail: MailPricing
}

export type BillingCycle = 'monthly' | 'yearly'

export interface Subscription {
  id: string
  user_id: string
  paddle_subscription_id?: string
  paddle_customer_id?: string
  plan: string
  billing_cycle?: BillingCycle
  mail_addon?: boolean
  status: string
  current_period_end?: string
  // ISO timestamp when a time-limited admin grant lapses; absent = permanent
  // grant or an ordinary self-serve paid subscription.
  plan_expires_at?: string
  canceled_at?: string
  extra_instances: number
  iyzico_card_last_four?: string
  iyzico_card_brand?: string
  charge_amount_minor?: number
  charge_currency?: string
  next_charge_at?: string
  last_charge_at?: string
  created_at: string
  updated_at: string
}

export interface Invoice {
  id: string
  user_id: string
  subscription_id?: string
  iyzico_payment_id?: string
  amount_minor: number
  currency: string
  status: 'paid' | 'failed'
  kind: 'initial' | 'renewal'
  billing_cycle?: BillingCycle
  description?: string
  created_at: string
}

// Catalog SKUs. The storefront's product list, detail pages and cart all key
// off these; names/descriptions are translated client-side.
export type ProductSKU = 'slot' | 'mail'

export interface CatalogProduct {
  sku: ProductSKU
  slug: string
  purchasable: boolean
  max_quantity: number
  needs_instance: boolean
  monthly_try: number
  yearly_try: number
  monthly_usd: number
  yearly_usd: number
  monthly_eur: number
  yearly_eur: number
  vat_included: boolean
  vat_rate: number
}

export interface CatalogResponse {
  products: CatalogProduct[]
  charge_currency: string
}

export interface CartCheckoutItem {
  sku: ProductSKU
  quantity: number
  instance_id?: string
}

// ── Billing API ────────────────────────────────────────────────────────────────

export const billingApi = {
  getCatalog: () => axios.get<CatalogResponse>('/api/v1/billing/catalog').then((r) => r.data),
  // Multi-line cart checkout. Prices are recomputed server-side from the
  // catalog — the payload only carries what was picked, never an amount.
  cartCheckout: (items: CartCheckoutItem[], cycle: BillingCycle, termsVersion: string) =>
    api
      .post<{ token: string; payment_page_url: string }>('/billing/checkout', {
        items,
        cycle,
        terms_accepted: true,
        terms_version: termsVersion,
      })
      .then((r) => r.data),
  getPlans: () => axios.get<PlansResponse>('/api/v1/billing/plans').then((r) => r.data),
  getSubscription: () => api.get<Subscription>('/billing/subscription').then((r) => r.data),
  getInvoices: () => api.get<Invoice[]>('/billing/invoices').then((r) => r.data),
  checkoutExtraInstance: (quantity: number, cycle: BillingCycle = 'monthly') =>
    api.post<{ token: string; payment_page_url: string }>('/billing/extra-instances', { quantity, cycle }).then((r) => r.data),
  deleteCard: () => api.delete<Subscription>('/billing/card').then((r) => r.data),
  updateCard: () =>
    api.post<{ token: string; payment_page_url: string }>('/billing/card/update', {}).then((r) => r.data),
}

// ── Auth (onboarding) API ──────────────────────────────────────────────────────

export const onboardingApi = {
  register: (email: string, companyName: string, phone: string, language?: string) =>
    axios.post('/api/v1/auth/register', { email, company_name: companyName, phone, language }).then((r) => r.data),
  forgotPassword: (email: string) =>
    axios.post('/api/v1/auth/forgot-password', { email }).then((r) => r.data),
  setPassword: (token: string, password: string) =>
    axios.post('/api/v1/auth/set-password', { token, password }).then((r) => r.data),
}

export const metaApi = {
  get: () => axios.get<{ support_email: string }>('/api/v1/meta').then((r) => r.data),
}

export const meApi = {
  get: () => api.get<PlatformUser>('/auth/me').then((r) => r.data),
  update: (body: Partial<Pick<PlatformUser, 'notify_backup_failed' | 'notify_provision_failed' | 'notify_instance_ready' | 'marketing_emails' | 'usage_tracking_enabled'> & { language?: string }>) =>
    api.patch('/auth/me', body),
  // Completes the contact identity a GitHub signup skipped (the register form
  // collects it for everyone else). Until this succeeds the API rejects every
  // feature call with 403 profile_incomplete.
  completeProfile: (company_name: string, phone: string) =>
    api.post('/auth/profile', { company_name, phone }),
  // Self-delete: the exit for a user who does not want to provide the
  // required info. Refused while the account still owns instances.
  deleteSelf: () => api.delete('/auth/me'),
}

export interface ApiKeyInfo {
  key?: string
  prefix?: string
  created_at?: string
  has_key: boolean
}

export const apiKeyApi = {
  get: () => api.get<ApiKeyInfo>('/me/api-key').then((r) => r.data),
  generate: () => api.post<ApiKeyInfo>('/me/api-key').then((r) => r.data),
  revoke: () => api.delete('/me/api-key'),
}

// Multi-token API auth (MCP server, CI bots). Token plaintext is returned only
// once at creation time; the prefix is the durable display value afterwards.
export interface ApiToken {
  id: string
  name: string
  prefix: string
  scopes: string[]
  expires_at?: string
  last_used_at?: string
  created_at: string
  // Present only on the create response.
  token?: string
}

export interface CreateApiTokenRequest {
  name: string
  scopes: string[]
  expires_in_days?: number
}

export const apiTokensApi = {
  list: () => api.get<ApiToken[]>('/me/tokens').then((r) => r.data ?? []),
  create: (body: CreateApiTokenRequest) =>
    api.post<ApiToken>('/me/tokens', body).then((r) => r.data),
  revoke: (id: string) => api.delete(`/me/tokens/${id}`),
  scopes: () => api.get<string[]>('/me/tokens/scopes').then((r) => r.data ?? []),
}

export interface MarketplaceApp {
  id: string
  slug: string
  name: string
  tagline: string
  description: string
  kind: 'downloadable' | 'installable'
  price_cents: number
  currency: string
  source_type: 'builtin' | 'github'
  source_ref: string
  github_ref: string
  icon: string
  version: string
}

export const marketplaceApi = {
  list: () => api.get<MarketplaceApp[]>('/apps').then((r) => r.data),
  get: (slug: string) => api.get<MarketplaceApp>(`/apps/${slug}`).then((r) => r.data),
  // Builtin apps gate the zip behind JWT, but <a> downloads can't set headers.
  // The Auth middleware accepts ?token= as a fallback (auth.go:26-28). The
  // caller is responsible for reading the token from the auth store.
  downloadUrl: (slug: string, token: string) =>
    `/api/v1/apps/${slug}/download?token=${encodeURIComponent(token)}`,
}

// ── Analytics types ────────────────────────────────────────────────────────────

export interface DailyStat {
  date: string
  visitors: number
  page_views: number
  signups: number
  subscriptions: number
}

export interface ProductDailyStat {
  date: string
  active_users: number
  app_page_views: number
  logins: number
}

export interface PageStat {
  path: string
  views: number
}

export interface ReferrerStat {
  referrer: string
  visitors: number
}

export interface GeoStat {
  country: string
  region?: string
  city?: string
  isp?: string
  lat?: number
  lng?: number
  visitors: number
  signups: number
}

export interface BotStats {
  bot_page_views: number
  human_page_views: number
  bot_visitors: number
  human_visitors: number
  bot_percentage: number
}

export interface PeriodComparison {
  prev_visitors: number
  prev_page_views: number
  prev_signups: number
  prev_subscriptions: number
  visitors_change: number
  page_views_change: number
  signups_change: number
  subscriptions_change: number
}

export interface AnalyticsSummary {
  marketing_page_views: number
  marketing_visitors: number
  marketing_sessions: number
  checkout_starts: number
  total_page_views: number
  unique_visitors: number
  total_sessions: number
  signups: number
  subscriptions: number
  visit_to_signup_rate: number
  signup_to_paid_rate: number
  visit_to_paid_rate: number
  product_active_users: number
  product_sessions: number
  app_page_views: number
  logins: number
  bounce_rate: number
  avg_pages_per_session: number
  new_visitors: number
  returning_visitors: number
  bot_traffic: BotStats
  period_comparison: PeriodComparison
  acquisition_stats: DailyStat[]
  product_stats: ProductDailyStat[]
  conversion_rate: number
  payment_conv_rate: number
  daily_stats: DailyStat[]
  top_pages: PageStat[]
  top_referrers: ReferrerStat[]
  top_countries: GeoStat[]
}

export interface CampaignStat {
  utm_source: string
  utm_medium: string
  utm_campaign: string
  visitors: number
  signups: number
  conversion_rate: number
}

export interface UserUsageStat {
  user_id: string
  email: string
  company_name: string
  page_views: number
  sessions: number
  last_active?: string
  top_path: string
}

export const analyticsApi = {
  summary: (range = 30) =>
    api.get<AnalyticsSummary>(`/analytics/summary?range=${range}`).then((r) => r.data),
  campaigns: (range = 30) =>
    api.get<CampaignStat[]>(`/analytics/campaigns?range=${range}`).then((r) => r.data),
  geo: (range = 30) =>
    api.get<GeoStat[]>(`/analytics/geo?range=${range}`).then((r) => r.data),
  // Per-user in-app usage (admin): who uses which screens, how much.
  users: (range = 30) =>
    api.get<UserUsageStat[]>(`/analytics/users?range=${range}`).then((r) => r.data),
}

// ── Usage & Revenue types ──────────────────────────────────────────────────────

export interface UsageRevenueUser {
  user_id: string
  email: string
  plan: string
  role: string
  instance_count: number
  running_count: number
  avg_cpu_percent: number
  avg_memory_bytes: number
  monthly_revenue: number
  extra_instances: number
  subscription_status: string
  created_at: string
  last_login?: string
}

export interface UsageRevenueDailyStat {
  date: string
  avg_cpu_percent: number
  avg_memory_bytes: number
  total_instances: number
  running_instances: number
  user_count: number
}

export interface BackupStorageInstance {
  instance_id: string
  subdomain: string
  name: string
  backup_bytes: number
  backup_count: number
  pre_op_count: number
}

export interface UsageRevenueSummary {
  total_users: number
  free_users: number
  paid_users: number
  total_revenue: number
  total_instances: number
  total_backup_bytes: number
  total_backup_count: number
  top_backup_instances: BackupStorageInstance[]
  users: UsageRevenueUser[]
  daily_stats: UsageRevenueDailyStat[]
}

export const usageRevenueApi = {
  summary: (range = 90, plan?: string) => {
    let url = `/usage-revenue?range=${range}`
    if (plan) url += `&plan=${plan}`
    return api.get<UsageRevenueSummary>(url).then((r) => r.data)
  },
}

// ── Infrastructure & Business dashboards (admin) ─────────────────────────────

export type RangeKey = '24h' | '7d' | '30d' | '90d'

// The monthly server bill (`server_budget`) is split across instances by the
// RAM each used over 30 days; `cost_basis` is the part that is split — net of
// VAT when `includes_vat` says the invoice VAT is reclaimed.
export interface CostRates {
  currency: string
  server_budget: number
  cost_basis: number
  includes_vat: boolean
  vat_percent: number
  server_cores: number
  server_ram_gb: number
  server_disk_gb: number
}

export interface HostSnapshot {
  cpu_percent: number
  mem_used: number
  mem_total: number
  disk_used: number
  disk_total: number
  container_count: number
}

export interface HostSample {
  captured_at: string
  cpu_percent: number
  mem_used_bytes: number
  mem_total_bytes: number
  disk_used_bytes: number
  disk_total_bytes: number
  container_count: number
}

export interface InfraAggregate {
  total_instances: number
  running_instances: number
  total_allocated_cores: number
  total_allocated_ram_gb: number
  total_storage_bytes: number
  total_backup_bytes: number
  total_backup_count: number
  server_monthly_budget_try: number
  cost_basis_try: number
  // cost_basis_try split by who owns the instance; they sum to it.
  cost_customer_try: number
  cost_platform_try: number
  server_total_cores: number
  server_total_ram_gb: number
  server_total_disk_gb: number
  // Overcommit signals: reserved (allocated) tenant RAM/CPU vs physical box.
  // >100% = oversold. Disk is actual-used, not reserved.
  ram_overcommit_pct: number
  cores_overcommit_pct: number
  disk_used_pct: number
  ram_free_gb: number
  cores_free: number
  instances_headroom: number
  typical_instance_ram_gb: number
}

export interface InfraInstanceRow {
  id: string
  subdomain: string
  name: string
  owner_email: string
  status: string
  cpu_percent: number
  mem_bytes: number
  mem_limit_bytes: number
  allocated_cores: number
  allocated_ram_gb: number
  storage_bytes: number
  net_30d_bytes: number
  uptime_percent: number
  mem_avg_30d_bytes: number
  cost_try_30d: number
  cost_bucket: CostBucket
}

// 'platform' = owned by an admin (demo / staging / internal); its cost is a
// platform expense, not a customer's.
export type CostBucket = 'customer' | 'platform'

export interface HostChartAnnotation {
  id: string
  ts: string
  body: string
  author_id?: string
  author_email: string
  created_at: string
}

export interface InfraOverview {
  host: HostSnapshot
  host_series: HostSample[]
  aggregate: InfraAggregate
  instances: InfraInstanceRow[]
  top_cpu: InfraInstanceRow[]
  top_storage: InfraInstanceRow[]
  annotations: HostChartAnnotation[]
  rates: CostRates
  window_days: number
}

export interface MetricTimeseriesPoint {
  instance_id: string
  container_role: string
  hour_bucket: string
  sample_count: number
  cpu_percent_avg: number
  cpu_percent_max: number
  mem_bytes_avg: number
  mem_bytes_max: number
  mem_limit_bytes: number
  net_rx_delta_bytes: number
  net_tx_delta_bytes: number
  blk_read_delta_bytes: number
  blk_write_delta_bytes: number
  running_seconds: number
}

export interface VolumeSnapshotPoint {
  instance_id: string
  day: string
  db_bytes: number
  filestore_bytes: number
  addons_bytes: number
  measured_at: string
}

export interface InfraInstanceTimeseries {
  series: MetricTimeseriesPoint[]
  volumes: VolumeSnapshotPoint[]
}

// Where an instance stands commercially, read from the operator's Odoo.
// paying: invoiced · trial: subscription open, not invoiced, prepaid period
// running · overdue: same, period over · unbilled: customer instance with no
// subscription · internal: our own (admin-owned) instance.
export type BillingStatus = 'paying' | 'trial' | 'overdue' | 'unbilled' | 'internal'
export type InvoiceState = 'none' | 'not_paid' | 'partial' | 'paid'

// All amounts are TRY, monthly, net of VAT.
export interface BusinessInstanceRow {
  id: string
  subdomain: string
  name: string
  owner_email: string
  status: BillingStatus
  subscription: string
  partner: string
  invoice_state: InvoiceState | ''
  prepaid_until: string
  mrr_try: number
  revenue_try: number
  cost_try: number
  margin_try: number
}

export interface BusinessStatusGroup {
  status: BillingStatus
  instances: number
  mrr_try: number
  revenue_try: number
  cost_try: number
  margin_try: number
}

export interface BusinessOverview {
  odoo: { configured: boolean; fetched_at?: string; error?: string }
  rates: CostRates
  instances: BusinessInstanceRow[]
  groups: BusinessStatusGroup[]
  totals: {
    revenue_try: number
    cost_try: number
    margin_try: number
    trial_mrr_try: number
    overdue_mrr_try: number
    unbilled_cost_try: number
    internal_cost_try: number
  }
}

export const infrastructureApi = {
  overview: (range: RangeKey = '24h') =>
    api.get<InfraOverview>(`/admin/infrastructure/overview?range=${range}`).then((r) => r.data),
  instanceTimeseries: (id: string, range: RangeKey = '24h') =>
    api.get<InfraInstanceTimeseries>(`/admin/infrastructure/instances/${id}/timeseries?range=${range}`).then((r) => r.data),
  createAnnotation: (ts: string, body: string) =>
    api.post<HostChartAnnotation>(`/admin/infrastructure/annotations`, { ts, body }).then((r) => r.data),
  deleteAnnotation: (id: string) =>
    api.delete(`/admin/infrastructure/annotations/${id}`).then(() => undefined),
}

// Server-side acquisition→revenue funnel (distinct users per stage).
// Sourced from first-party business_events — immune to ad-blockers & consent.
export interface BusinessFunnel {
  registered: number
  created_instance: number
  started_checkout: number
  paid: number
  canceled: number
}

export const businessApi = {
  overview: () =>
    api.get<BusinessOverview>('/admin/business/overview').then((r) => r.data),
  funnel: (range: RangeKey = '30d') =>
    api.get<BusinessFunnel>(`/admin/business/funnel?range=${range}`).then((r) => r.data),
  // Opens (or finds) the Odoo sale behind a customer's instance.
  openSale: (instanceId: string) =>
    api.post<{ order: string; partner: string; existing: boolean }>(`/admin/business/instances/${instanceId}/open-sale`).then((r) => r.data),
}

// Platform settings (admin): company/legal info + PayTR credentials.
// Secrets are never returned — only "_set" booleans indicate presence.
export interface CompanyInfo {
  'company.legal_name': string
  'company.trade_name': string
  'company.address': string
  'company.city': string
  'company.phone': string
  'company.email': string
  'company.tax_office': string
  'company.tax_no': string
  'company.mersis_no': string
  'company.kep': string
  'company.website': string
  'company.legal_email': string
  'company.hosting_notice': string
}

export interface PlatformSettings {
  company: CompanyInfo
  paytr_merchant_id: string
  paytr_test_mode: boolean
  paytr_merchant_key_set: boolean
  paytr_merchant_salt_set: boolean
}

export interface UpdatePlatformSettingsRequest {
  company?: Partial<CompanyInfo>
  paytr_merchant_id?: string
  // Omit or send empty to keep the stored secret unchanged.
  paytr_merchant_key?: string
  paytr_merchant_salt?: string
  paytr_test_mode?: boolean
}

export const platformSettingsApi = {
  get: () =>
    api.get<PlatformSettings>('/admin/platform-settings').then((r) => r.data),
  update: (payload: UpdatePlatformSettingsRequest) =>
    api.put<PlatformSettings>('/admin/platform-settings', payload).then((r) => r.data),
}

// Public company info for legal/contact/footer pages (no PayTR).
export const companyInfoApi = {
  get: () =>
    api.get<{ company: CompanyInfo }>('/company-info').then((r) => r.data),
}

// 5651 traffic-log archive (admin, read-only metadata — nothing here can
// decrypt anything; extraction stays on the host via extract-5651.sh).
export interface TrafficLogRun {
  tag: string
  date: string
  manifest: boolean
  stamped: boolean
  files: number
  bytes: number
}
export interface TrafficLogHost {
  host: string
  files: number
  bytes: number
  first_date: string
  last_date: string
}
export interface TrafficLogsOverview {
  configured: boolean
  runs?: TrafficLogRun[]
  hosts?: TrafficLogHost[]
  issues?: string[]
  total_bytes?: number
  other_files?: number
}
export const trafficLogsApi = {
  overview: () =>
    api.get<TrafficLogsOverview>('/admin/traffic-logs').then((r) => r.data),
}

export function getPlatformSettings() {
  return platformSettingsApi.get()
}
export function updatePlatformSettings(payload: UpdatePlatformSettingsRequest) {
  return platformSettingsApi.update(payload)
}
export function getCompanyInfo() {
  return companyInfoApi.get()
}

// Odoo/PostgreSQL image catalogue (admin) — which image each series uses and
// which instance runs what. See internal-docs plan: odoo-image-catalog.
export interface PlatformImage {
  id: string
  kind: 'odoo' | 'postgres'
  series: string
  ref: string
  is_default: boolean
  enabled: boolean
  released_at?: string
  notes: string
  rolling: boolean
  instance_count: number
}

export interface ImageUpstreamCheck {
  kind: string
  series: string
  latest_ref: string
  latest_released_at?: string
  checked_at: string
}

export interface ImageCatalog {
  images: PlatformImage[]
  upstream: ImageUpstreamCheck[]
}

export const imagesApi = {
  catalog: () => api.get<ImageCatalog>('/admin/images').then((r) => r.data),
  available: () => api.get<PlatformImage[]>('/images/available').then((r) => r.data),
  create: (ref: string, notes: string) => api.post('/admin/images', { ref, notes }),
  update: (id: string, enabled: boolean, notes: string) =>
    api.patch(`/admin/images/${id}`, { enabled, notes }),
  setDefault: (id: string) => api.post(`/admin/images/${id}/default`),
  remove: (id: string) => api.delete(`/admin/images/${id}`),
  swap: (instanceId: string, imageId: string) =>
    api.post(`/instances/${instanceId}/image`, { image_id: imageId }),
  rollback: (instanceId: string) => api.post(`/instances/${instanceId}/image/rollback`),
}

// Odoo major-version upgrade (19 -> 20). One open upgrade per instance; every
// action is admin-only, the GET is readable by the owner without engine logs.
// docs/superpowers/specs/2026-10-01-odoo-major-upgrade-design.md
export type UpgradeState =
  | 'rehearsal' | 'rehearsal_approved' | 'production' | 'observing'
  | 'committed' | 'rolled_back' | 'failed' | 'cancelled'

export type UpgradeEngine = 'openupgrade' | 'external_dump'

export interface InstanceUpgrade {
  id: string
  instance_id: string
  rehearsal_instance_id?: string
  from_series: string
  to_series: string
  target_image_id: string
  engine: UpgradeEngine
  state: UpgradeState
  stage: string
  error?: string
  logs?: string
  prev_db_name?: string
  new_db_name?: string
  baseline_errors_per_hour?: number
  baseline_failed_crons?: number
  pre_op_backup_id?: string
  cutover_at?: string
  observe_until?: string
  closed_at?: string
  created_at: string
  updated_at: string
}

export interface UpgradeObservation {
  observed_at: string
  errors_per_hour: number
  failed_crons: number
  restart_count: number
}

export interface UpgradeStatus {
  upgrade: InstanceUpgrade | null
  rehearsal?: {
    id: string
    subdomain: string
    url: string
    status: string
    odoo_version: string
    addons: { name: string; version: string; source_url?: string }[]
  }
  observations: UpgradeObservation[]
  verdict?: { red: boolean; reasons: string[] }
}

export interface UpgradePrecheck {
  ok: boolean
  from_series: string
  target_series?: string
  target_image?: PlatformImage
  engines: UpgradeEngine[]
  required_bytes: number
  free_bytes: number
  problems: string[]
}

export const upgradeApi = {
  status: (id: string) => api.get<UpgradeStatus>(`/instances/${id}/upgrade`).then((r) => r.data),
  precheck: (id: string) => api.post<UpgradePrecheck>(`/instances/${id}/upgrade/precheck`).then((r) => r.data),
  start: (id: string, engine: UpgradeEngine) => api.post(`/instances/${id}/upgrade`, { engine }),
  prepareCode: (id: string, refs: Record<string, string>) => api.post(`/instances/${id}/upgrade/code`, { refs }),
  uploadDump: (id: string, file: File) => {
    const form = new FormData()
    form.append('file', file)
    return api.post(`/instances/${id}/upgrade/dump`, form)
  },
  approve: (id: string) => api.post(`/instances/${id}/upgrade/approve`),
  production: (id: string) => api.post(`/instances/${id}/upgrade/production`),
  rollback: (id: string) => api.post(`/instances/${id}/upgrade/rollback`),
  commit: (id: string) => api.post(`/instances/${id}/upgrade/commit`),
  cancel: (id: string) => api.post(`/instances/${id}/upgrade/cancel`),
}
